/**
 * Branded, prefixed ids.
 *
 * Shape: `<prefix>_<26 chars>` where the 26 chars are a ULID in Crockford base32
 * (10 chars of millisecond timestamp + 16 chars of randomness). Ids therefore sort
 * by creation time as plain strings, which keeps SQLite/D1 primary-key inserts
 * append-mostly.
 *
 * The brand exists only at the type level: a `MemberId` is still a `string` at
 * runtime, but TypeScript will not let you pass a `StaffId` where a `MemberId`
 * is expected. Get a branded id from `newId` (fresh) or `parseId` (untrusted input).
 */

export const ID_PREFIXES = {
  business: "biz",
  brand: "brd",
  branch: "brn",
  person: "per",
  member: "mem",
  staff: "stf",
  role: "rol",
  device: "dev",
  audit: "aud",
  item: "itm",
  coupon: "cpn",
  // billing slice rails (wave 2)
  bill: "bil",
  billLine: "bln",
  planInstance: "pln",
  payment: "pay",
  refund: "rfd",
  creditEntry: "mcr",
  commissionEvent: "cev",
  // invoices
  invoice: "inv",
  creditNote: "crn",
  // payments
  paymentAccount: "pac",
  paymentIntent: "pin",
  webhookEvent: "wbh",
  // membership-actions
  freeze: "frz",
  planAction: "pla",
  // gate rails (wave 3): pt-classes
  classTemplate: "clt",
  classBooking: "clb",
  // payroll
  commissionRule: "cmr",
  ledgerLine: "cml",
  payrollMonth: "prm",
  payslip: "psl",
  advance: "adv",
  // staff-attendance
  punch: "pch",
  // kiosk
  kiosk: "ksk",
  checkin: "chk",
  checkinKey: "ckk",
  // lead-intake
  lead: "lea",
  intakeKey: "lwh",
  // platform rails: messaging
  messageLog: "msg",
  template: "tpl",
  waConnection: "wac",
  // data-safety
  migrationRun: "mrn",
  backupObject: "bko",
  restoreRun: "rst",
  // onboarding
  importJob: "imj",
  importFile: "imf",
  sandbox: "sbx",
  // biometric-bridge
  bridge: "bdg",
  terminal: "trm",
  deviceUser: "dus",
  biometricConsent: "bcn",
  bridgeCommand: "bcm",
  biometricWipe: "bwp",
  biometricCopy: "bcp",
  devicePunch: "dpn",
  // expenses-reports
  expense: "exp",
  expenseCategory: "exc",
  expensePayee: "pye",
  recurringExpense: "rex",
  attachment: "att",
  // followups-tasks-api
  task: "tsk",
  appointment: "apt",
  // inbox-api
  inboxConversation: "cnv",
  inboxMessage: "ixm",
  // staff-documents
  staffDocument: "sdc",
  staffDocumentLink: "sdl",
} as const;

export type IdKind = keyof typeof ID_PREFIXES;
export type IdPrefix = (typeof ID_PREFIXES)[IdKind];

declare const idBrand: unique symbol;
/** A string id branded with its kind. */
export type Id<K extends IdKind> = string & { readonly [idBrand]: K };

export type BusinessId = Id<"business">;
export type BrandId = Id<"brand">;
export type BranchId = Id<"branch">;
export type PersonId = Id<"person">;
export type MemberId = Id<"member">;
export type StaffId = Id<"staff">;
export type RoleId = Id<"role">;
export type DeviceId = Id<"device">;
export type AuditId = Id<"audit">;
export type ItemId = Id<"item">;
export type CouponId = Id<"coupon">;
export type BillId = Id<"bill">;
export type BillLineId = Id<"billLine">;
export type PlanInstanceId = Id<"planInstance">;
export type PaymentId = Id<"payment">;
export type RefundId = Id<"refund">;
export type CreditEntryId = Id<"creditEntry">;
export type CommissionEventId = Id<"commissionEvent">;
export type ClassTemplateId = Id<"classTemplate">;
export type ClassBookingId = Id<"classBooking">;
export type CommissionRuleId = Id<"commissionRule">;
export type LedgerLineId = Id<"ledgerLine">;
export type PayrollMonthId = Id<"payrollMonth">;
export type PayslipId = Id<"payslip">;
export type AdvanceId = Id<"advance">;
export type PunchId = Id<"punch">;
export type KioskId = Id<"kiosk">;
export type CheckinId = Id<"checkin">;
export type CheckinKeyId = Id<"checkinKey">;
export type LeadId = Id<"lead">;
export type IntakeKeyId = Id<"intakeKey">;
export type MessageLogId = Id<"messageLog">;
export type TemplateId = Id<"template">;
export type WaConnectionId = Id<"waConnection">;
export type MigrationRunId = Id<"migrationRun">;
export type BackupObjectId = Id<"backupObject">;
export type RestoreRunId = Id<"restoreRun">;
export type ImportJobId = Id<"importJob">;
export type ImportFileId = Id<"importFile">;
export type SandboxId = Id<"sandbox">;
export type BridgeId = Id<"bridge">;
export type TerminalId = Id<"terminal">;
export type DeviceUserId = Id<"deviceUser">;
export type BiometricConsentId = Id<"biometricConsent">;
export type BridgeCommandId = Id<"bridgeCommand">;
export type BiometricWipeId = Id<"biometricWipe">;
export type BiometricCopyId = Id<"biometricCopy">;
export type DevicePunchId = Id<"devicePunch">;
export type ExpenseId = Id<"expense">;
export type ExpenseCategoryId = Id<"expenseCategory">;
export type ExpensePayeeId = Id<"expensePayee">;
export type RecurringExpenseId = Id<"recurringExpense">;
export type AttachmentId = Id<"attachment">;
export type TaskId = Id<"task">;
export type AppointmentId = Id<"appointment">;
export type InboxConversationId = Id<"inboxConversation">;
export type InboxMessageId = Id<"inboxMessage">;

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const TIME_LEN = 10;
const RANDOM_LEN = 16;
const ULID_RE = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;
const MAX_TIME = 2 ** 48 - 1;

export interface NewIdOptions {
  /** Epoch milliseconds to encode. Defaults to `Date.now()`. Inject a Clock value in tests. */
  now?: number;
  /** Source of 16 random bytes. Defaults to `crypto.getRandomValues`. */
  randomBytes?: (n: number) => Uint8Array;
}

function defaultRandomBytes(n: number): Uint8Array {
  const c = (globalThis as unknown as { crypto?: { getRandomValues(a: Uint8Array): Uint8Array } }).crypto;
  if (!c) throw new Error("newId: no crypto.getRandomValues in this runtime; pass options.randomBytes");
  return c.getRandomValues(new Uint8Array(n));
}

function encodeTime(ms: number): string {
  if (!Number.isInteger(ms) || ms < 0 || ms > MAX_TIME) {
    throw new RangeError(`newId: timestamp out of range: ${ms}`);
  }
  let out = "";
  let t = ms;
  for (let i = 0; i < TIME_LEN; i++) {
    out = CROCKFORD[t % 32] + out;
    t = Math.floor(t / 32);
  }
  return out;
}

function encodeRandom(bytes: Uint8Array): string {
  // 16 chars x 5 bits = 80 bits = 10 bytes. Use the low 5 bits of 16 bytes:
  // simple, unbiased (32 divides 256), and clearer than bit-packing.
  if (bytes.length < RANDOM_LEN) throw new RangeError("newId: randomBytes returned too few bytes");
  let out = "";
  for (let i = 0; i < RANDOM_LEN; i++) out += CROCKFORD[bytes[i]! & 31];
  return out;
}

/** Generate a fresh id of the given kind, e.g. `newId("member")` → `mem_01JB…`. */
export function newId<K extends IdKind>(kind: K, options: NewIdOptions = {}): Id<K> {
  const now = options.now ?? Date.now();
  const bytes = (options.randomBytes ?? defaultRandomBytes)(RANDOM_LEN);
  return `${ID_PREFIXES[kind]}_${encodeTime(now)}${encodeRandom(bytes)}` as Id<K>;
}

/** True if `value` is a well-formed id of `kind`. */
export function isId<K extends IdKind>(kind: K, value: unknown): value is Id<K> {
  if (typeof value !== "string") return false;
  const prefix = ID_PREFIXES[kind];
  if (value.length !== prefix.length + 1 + TIME_LEN + RANDOM_LEN) return false;
  if (!value.startsWith(prefix + "_")) return false;
  return ULID_RE.test(value.slice(prefix.length + 1));
}

/** Parse untrusted input into a branded id. Returns `null` if malformed or of another kind. */
export function parseId<K extends IdKind>(kind: K, value: unknown): Id<K> | null {
  return isId(kind, value) ? value : null;
}

/** Which kind an id string is, by its prefix, or `null` if unrecognised/malformed. */
export function idKindOf(value: unknown): IdKind | null {
  if (typeof value !== "string") return null;
  for (const kind of Object.keys(ID_PREFIXES) as IdKind[]) {
    if (isId(kind, value)) return kind;
  }
  return null;
}

/** Epoch milliseconds encoded in an id (its creation time). */
export function idTimestamp(id: Id<IdKind>): number {
  const ulid = id.slice(id.indexOf("_") + 1);
  let t = 0;
  for (let i = 0; i < TIME_LEN; i++) t = t * 32 + CROCKFORD.indexOf(ulid[i]!);
  return t;
}
